/**
 * What a person without a photo looks like: their initials on a colour that
 * belongs to their name.
 *
 * This used to be an `<img>` pointing at ui-avatars.com. On 2026-10-01 that
 * service stopped answering and every user without a photo turned into a
 * broken-image icon in all three apps at once — and every page view had been
 * sending people's names (workshop customers' included) to a third party we
 * have no agreement with. Drawing two letters needs no service.
 *
 * Pure functions, no React: the `Avatar` component renders what these decide,
 * and `tenant/frontend/hooks/avatar.test.ts` pins them.
 */

/** Background + text classes for one avatar colour. White text on every
 *  background, so the palette reads the same in light and dark mode. Kept as
 *  literal strings: Tailwind only ships classes it can find in the source. */
export const AVATAR_PALETTE: readonly string[] = [
    'bg-rose-500 text-white',
    'bg-orange-500 text-white',
    'bg-amber-600 text-white',
    'bg-emerald-600 text-white',
    'bg-teal-600 text-white',
    'bg-sky-600 text-white',
    'bg-indigo-500 text-white',
    'bg-fuchsia-600 text-white',
];

const LETTER = /\p{L}/u;
const DIGIT = /\p{N}/u;

/** The part of the name worth reading: an email loses its domain, so
 *  `ana@taller.es` reads "A" and not "AT". */
function readableName(name: string | null | undefined): string {
    const trimmed = (name ?? '').trim();
    // `@handle` is a username, not an email: only a leading part counts.
    const at = trimmed.indexOf('@');
    if (!trimmed.includes(' ') && at > 0) return trimmed.slice(0, at);
    return trimmed;
}

/**
 * Up to two initials: the first letter of the first two words that have one.
 * Usernames split on `.`, `_` and `-` too, so "job_pautorrent" → "JP".
 * "Pau Torrent Carrillo" → "PT", "álvaro" → "Á", "arnau_1996" → "A".
 * No letter at all → the first digit; nothing readable → "?".
 */
export function avatarInitials(name: string | null | undefined): string {
    const words = readableName(name).split(/[\s._-]+/);
    const initials: string[] = [];
    for (const word of words) {
        const first = Array.from(word).find((ch) => LETTER.test(ch));
        if (first) initials.push(first.toLocaleUpperCase());
        if (initials.length === 2) break;
    }
    if (initials.length > 0) return initials.join('');
    const digit = Array.from(words.join('')).find((ch) => DIGIT.test(ch));
    return digit ?? '?';
}

/**
 * The palette entry for a name. Deterministic (FNV-1a over the lower-cased
 * name), so a person keeps their colour across reloads, lists and apps.
 */
export function avatarColorClass(name: string | null | undefined): string {
    const key = readableName(name).toLocaleLowerCase();
    let hash = 0x811c9dc5;
    for (let i = 0; i < key.length; i++) {
        hash ^= key.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }
    return AVATAR_PALETTE[(hash >>> 0) % AVATAR_PALETTE.length] ?? AVATAR_PALETTE[0]!;
}
